import React from 'react';
import { ArrowLeft, MessageCircle, RefreshCw, Activity } from 'lucide-react';

import AnimatedBackground from '../Background';
import useWhatsappConfig from './useWhatsappConfig';

import WhatsappConfigList   from './WhatsappConfigList';
import WhatsappQueuePanel   from './WhatsappQueuePanel';
import WhatsappConfigForm   from './WhatsappConfigForm';
import WhatsappDetailsModal from './WhatsappDetailsModal';
import { AlertBanner, LicenseModal, ConfirmModal } from './WhatsappConfigComponents';

export default function WhatsAppConfig({ onBack, API_BASE_URL, loginId, deliveryMode }) {
    const state = useWhatsappConfig({ API_BASE_URL, loginId, deliveryMode });

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 to-[#1a6b4a] font-inter text-white">
            <AnimatedBackground />

            <div className="relative z-10 p-6 md:p-10 max-w-7xl mx-auto">

                {/* ── Header ─────────────────────────────────────────────── */}
                <div className="flex items-center justify-between mb-10">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onBack}
                            className="flex items-center space-x-2 px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            <span>Back to Dashboard</span>
                        </button>
                        <div className="flex items-center space-x-4">
                            <div className="w-14 h-14 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                                <MessageCircle className="w-7 h-7" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold tracking-tight">WhatsApp Configuration</h1>
                                <p className="text-white/70 text-sm">Manage your WhatsApp service settings</p>
                            </div>
                        </div>
                    </div>

                    {/* Refresh buttons */}
                    <div className="flex items-center space-x-3">
                        <button onClick={state.loadQueueType} disabled={state.isLoadingQueue} className="flex items-center space-x-2 px-4 py-2 bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/40 rounded-xl text-orange-200 transition-all disabled:opacity-50">
                            <RefreshCw className={`w-4 h-4 ${state.isLoadingQueue ? 'animate-spin' : ''}`} />
                            <span>Queue Status</span>
                        </button>
                        <button onClick={state.loadAppStatuses} disabled={state.isLoadingAppStatuses} className="flex items-center space-x-2 px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 rounded-xl text-cyan-200 transition-all disabled:opacity-50">
                            <Activity className={`w-4 h-4 ${state.isLoadingAppStatuses ? 'animate-spin' : ''}`} />
                            <span>App Status</span>
                        </button>
                        <button onClick={state.handleRefresh} disabled={state.isLoading} className="flex items-center space-x-2 px-5 py-3 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-600/40 rounded-xl text-emerald-200 transition-all disabled:opacity-50 shadow-md">
                            <RefreshCw className={`w-5 h-5 ${state.isLoading ? 'animate-spin' : ''}`} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* ── Global Alerts ───────────────────────────────────────── */}
                {(state.success || state.error || state.queueSuccess || state.queueError) && (
                    <div className="mb-6 space-y-3">
                        <AlertBanner success={state.success} error={state.error} />
                        <AlertBanner success={state.queueSuccess} error={state.queueError} />
                    </div>
                )}

                {/* ── Main Grid ───────────────────────────────────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    {/* Left — Config List */}
                    <div className="lg:col-span-1">
                        <WhatsappConfigList
                            configs={state.configs}
                            isLoading={state.isLoading}
                            appNameStatuses={state.appNameStatuses}
                            isLoadingAppStatuses={state.isLoadingAppStatuses}
                            isUpdatingAppStatus={state.isUpdatingAppStatus}
                            updatingAppName={state.updatingAppName}
                            selectedConfig={state.selectedConfig}
                            onSelect={(config) => {
                                state.setSelectedConfig(config);
                                state.setIsDetailsModalOpen(true);
                            }}
                            onToggleAppStatus={state.handleToggleAppNameStatus}
                        />
                    </div>

                    {/* Right — Queue + Form */}
                    <div className="lg:col-span-2 space-y-6">
                        <WhatsappQueuePanel
                            currentQueueType={state.currentQueueType}
                            isLoadingQueue={state.isLoadingQueue}
                            selectedQueueType={state.selectedQueueType}
                            setSelectedQueueType={state.setSelectedQueueType}
                            isUpdatingQueue={state.isUpdatingQueue}
                            onUpdateQueue={state.handleUpdateQueueType}
                        />
                        <WhatsappConfigForm
                            formData={state.formData}
                            isEditing={state.isEditing}
                            isSaving={state.isSaving}
                            showPassword={state.showPassword}
                            onShowPassword={() => state.setShowPassword(p => !p)}
                            onChange={state.handleInputChange}
                            onSave={state.handleSaveConfig}
                            onCancel={state.resetForm}
                        />
                    </div>
                </div>
            </div>

            {/* ── Modals ─────────────────────────────────────────────────── */}
            <WhatsappDetailsModal
                isOpen={state.isDetailsModalOpen}
                onClose={() => state.setIsDetailsModalOpen(false)}
                config={state.selectedConfig}
                showPassword={state.showPassword}
                onTogglePassword={() => state.setShowPassword(p => !p)}
                isUpdating={state.isUpdating}
                configToUpdate={state.configToUpdate}
                onEdit={state.loadConfigForEdit}
                onToggleStatus={state.handleToggleClick}
            />

            <LicenseModal
                isOpen={state.isLicenseModalOpen}
                onClose={() => state.setIsLicenseModalOpen(false)}
                licenseNo={state.licenseNo}
                setLicenseNo={state.setLicenseNo}
                licenseError={state.licenseError}
                onSubmit={state.handleLicenseSubmit}
            />

            <ConfirmModal
                isOpen={state.isConfirmModalOpen}
                onClose={() => state.setIsConfirmModalOpen(false)}
                config={state.configToUpdate}
                onConfirm={state.executeStatusUpdate}
                isUpdating={state.isUpdating}
            />
        </div>
    );
}